<template>
  <div class="view-more-layout sub-category">
    <portal-target name="filter-overlay-list" class="overlay-portal" />

    <div ref="contentsOverlay" class="view-more-contents">
      <div
        class="common-header"
        :style="{
          backgroundColor: tonal.gray[800],
        }"
      >
        <div class="top-section">
          <SubCategoryMenu
            v-if="hasSubCategory"
            :title="title"
            :options="subCategoryRef"
            :close-fn="onClickBack"
            @change="onChangeSubCategory"
          />
        </div>

        <div v-if="hasFilter" class="filter-wrapper">
          <div
            v-for="filter in filterListRef"
            :key="filter.key"
            class="side-filter-wrapper"
          >
            <SideFilter
              :options="filter"
              :selected-value="queryRef[filter.key]"
              @change="onChangeSideFilter"
            />
          </div>
        </div>
      </div>

      <div class="view-more-component-container" data-scroll-element="true">
        <infinite-scroll-cell-list
          v-if="hasCellList"
          :key="`${originQuery.mainCode}__${originQuery.subCode}__list`"
          :cell-list="contextList"
          :thumbnail-info="thumbnailInfo"
          :container-class-name="[
            'cell-list',
            'responsive',
            `cell-${thumbnailType}`,
          ]"
          @load-more="loadMore"
        />

        <div
          v-else-if="(!isLoading && !hasCellList) || isError"
          class="empty-cell-list"
        >
          <div class="attention-mark-area">
            <EmptyImage />
          </div>

          <div class="text-area">해당데이터가 없습니다.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router/composables'
  import { tonal } from '@/constants/tonal'
  import { PATH_NAME_HOME } from '@/common/router.const'
  import { getBandTypeInfoByCellType } from '@/common/image.util'
  import { stringify, queryStringToObject } from '@/common/util/shared'
  import { getCategorySubHomeData } from '@/common/requests/data-fetch/get-category-sub-home-data'
  import { useCategoryFilter } from '@/common/use/useCategoryFilter'
  import { useCategorySub } from '@/common/use/useCategorySub'
  import { useSideFilter } from '@/common/use/useSideFilter'
  import { isEmptyObject } from '@/common/converter/utils/is-empty-object'
  import { getFetchUrl } from '@/common/util/home/get-fetch-url'

  import SideFilter from '@/components/category/Sub/Filter/SideFilter'
  import InfiniteScrollCellList from '@/components/section/template/cell/InfiniteScrollCellList'
  import SubCategoryMenu from '@/components/category/Sub/Header/SubCategoryMenu'
  import EmptyImage from '@/assets/svg/common/img_empty.svg'

  const { fetchData, setApiType, updateRouter, replaceRouter, goMainCategory } =
    useCategorySub()
  const { title, subCategoryRef, setCategoryFilter } = useCategoryFilter()
  const { filterListRef, getAppendQuery, getSideFilterQuery } = useSideFilter()

  const router = useRouter()
  const route = useRoute()

  // api call url, queries
  const apiUrl = ref('')
  const apiQuery = ref({})
  const fetchUrl = ref('')
  const originUrl = ref('')
  const appendQuery = ref({})
  const originQuery = ref({})
  const queryRef = ref(null)
  const main = ref('')
  const sub = ref('')

  // ui data
  const cellType = ref('')
  const contextList = ref([])
  const homeData = ref({})
  const limit = ref(20)
  const isEndOfList = ref(false)
  const isError = ref(false)
  const isLoading = ref(true)
  const offset = ref(0)
  const titleList = ref([])
  const topButtonList = ref([])
  const thumbnailInfo = ref(null)
  const thumbnailType = ref('')

  const hasSubCategory = computed(() => subCategoryRef.value !== null)
  const hasCellList = computed(() => contextList.value?.length > 0)
  const hasFilter = computed(() => filterListRef.value?.length > 0)

  /**
   * on click back (to home)
   * @returns {Promise<Route>}
   */
  const onClickBack = () => {
    return router.push({
      name: PATH_NAME_HOME,
    })
  }

  /**
   * sub category filter 변경 이벤트
   * @param event
   * @returns {undefined|void}
   */
  const onChangeSubCategory = (event) => {
    if (!event) {
      return undefined
    }

    main.value = originQuery.value?.mainCode ?? ''
    sub.value = event?.value ?? ''

    if (main.value === event?.value) {
      return goMainCategory({ main: main.value })
    }

    isLoading.value = true
    updateRouter({ mainCode: main.value, subCode: sub.value })
  }

  /**
   * side filter 변경 이벤트
   * @param key
   * @param value
   */
  const onChangeSideFilter = ({ key, value }) => {
    if (!key) {
      return undefined
    }

    setQuery({
      ...queryRef.value,
      offset: 0,
      limit: limit.value,
      [key]: value,
    })

    appendQuery.value = {
      ...appendQuery.value,
      [key]: value,
    }

    updateRouter({
      ...originQuery.value,
      ...appendQuery.value,
    })
  }

  /**
   * side filter 세팅
   * @param filter
   */
  const setFilterValues = (filter) => {
    filterListRef.value = filter?.filterList || []
    appendQuery.value = getAppendQuery(originQuery.value)

    queryRef.value = {
      ...appendQuery.value,
      ...queryRef.value,
    }
  }

  /**
   * filter 초기화
   * 필터 변경 후 뒤로가기 시 초기화된 offset 데이터로 filter 를 초기화
   */
  const resetFilter = () => {
    queryRef.value = setOriginQuery()
    setFilterValues(homeData.value?.layout?.filter)
    setQuery(queryRef.value)
  }

  /**
   * context (cell) list 업데이트 - 초기화 후 데이터 세팅
   * @returns {Promise<void>}
   */
  const updateContextList = async () => {
    window.scrollTo(0, 0)
    clearData()
    resetFilter()

    await setData()
  }

  /**
   * context (cell) list 초기화
   * offset 초기화
   */
  const clearData = () => {
    contextList.value = []
    filterListRef.value = []
    offset.value = 0
  }

  /**
   * data fetch 이후 UI 세팅
   * @param data
   */
  const processListData = (data) => {
    if (!data) {
      return undefined
    }

    titleList.value = data?.cell_toplist?.title_list || []
    thumbnailInfo.value = getBandTypeInfoByCellType(
      data?.cell_toplist?.cell_type || '',
    )
    topButtonList.value = data?.cell_toplist?.topbuttonlist || []
    contextList.value = [...contextList.value, ...data?.cell_toplist?.celllist]
    cellType.value = data?.cell_toplist?.cell_type ?? []
    isEndOfList.value = data?.cell_toplist?.celllist?.length === 0
    thumbnailType.value = thumbnailInfo.value?.type
  }

  /**
   * api 호출 url 을 home Api 에서 가져와
   * origin query, api query, offset, limit 세팅
   * @returns {{offset: UnwrapRef<number>, limit: UnwrapRef<number>}|undefined}
   */
  const setOriginQuery = () => {
    originUrl.value = route.path
    originQuery.value = route.query

    const url = getFetchUrl(homeData.value)
    if (!url) {
      return undefined
    }

    apiUrl.value = url?.split('?')?.[0]
    apiQuery.value = queryStringToObject(url)
    offset.value = Number(apiQuery.value?.offset) ?? 0
    limit.value = Number(apiQuery.value?.limit) ?? 20

    return {
      ...apiQuery.value,
      ...originQuery.value,
      offset: offset.value,
      limit: limit.value,
    }
  }

  /**
   * queryRef 값과 fetchUrl 값 세팅
   * @param query
   */
  const setQuery = (query) => {
    if (!query) {
      return
    }

    queryRef.value = {
      ...queryRef.value,
      ...query,
    }

    fetchUrl.value = `${apiUrl.value}?${stringify(queryRef.value)}`
  }

  /**
   * main, sub 외의 filter 관련 파라미터가 없다면 filter 의 초기 값으로 라우터 갱신 - 쿼리 워치 후 갱신에 사용
   */
  const updateQuery = () => {
    const originSideFilter = getSideFilterQuery(originQuery.value)

    if (isEmptyObject(originSideFilter)) {
      replaceRouter({
        ...originQuery.value,
        ...appendQuery.value,
      })
    }
  }

  /**
   * fetch data 를 통해
   * contextList (cell_list) 를 세팅
   * @returns {Promise<void>}
   */
  const setData = async () => {
    isLoading.value = true

    const data = await fetchData({
      fetchUrl: fetchUrl.value,
      homeData: homeData.value,
    })

    if (!data) {
      isError.value = true
      return undefined
    }

    processListData(data)
    isLoading.value = false
  }

  /**
   * load more
   * @returns {undefined}
   */
  const loadMore = () => {
    if (isEndOfList.value) {
      return undefined
    }

    offset.value += limit.value
    setQuery({
      ...apiQuery.value,
      ...appendQuery.value,
      offset: offset.value,
      limit: limit.value,
    })
    setData()
  }

  /**
   * filter 변경 초기화 시퀀스 (홈 api 호출하지 않는 초기화)
   */
  const filterChangedInit = () => {
    setQuery({
      ...queryRef.value,
      ...route.query,
    })
    updateContextList()
  }

  /**
   * init (홈 api 호출 하는 초기화)
   * @param mainCategory
   * @param subCategory
   * @returns {Promise<void>}
   */
  const initialize = async (mainCategory, subCategory) => {
    if (!mainCategory || !subCategory) {
      isError.value = true
      return undefined
    }

    const { data } = await getCategorySubHomeData({ mainCategory, subCategory })

    if (!data) {
      isError.value = true
      return undefined
    }
    const { catalog, category, filter } = data?.layout
    homeData.value = data

    setCategoryFilter({ category, main: mainCategory, sub: subCategory })
    setApiType(catalog)
    queryRef.value = setOriginQuery()
    setFilterValues(filter)
    setQuery(queryRef.value)
    updateQuery()
    await setData()
  }

  const init = () => {
    const { mainCode, subCode } = router.currentRoute.query
    main.value = mainCode
    sub.value = subCode

    clearData()
    initialize(main.value, sub.value)
  }

  init()

  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      const isSubCategoryChanged = oldQuery.subCode !== newQuery.subCode
      const isFilterChanged =
        Object.keys(oldQuery).length === Object.keys(newQuery).length

      if (isSubCategoryChanged) {
        init()
        return
      }

      if (isFilterChanged) {
        filterChangedInit()
      }
    },
  )
</script>

<style lang="scss" scoped>
  .overlay-portal {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .empty-cell-list {
    position: relative;
    height: 250px;
    width: 250px;
    margin: 0 auto;
    top: 70px;

    .attention-mark-area {
      height: 50px;
      width: 50px;
      position: relative;
      margin: 0 auto;
    }

    .text-area {
      text-align: center;
      margin-top: 16px;
      color: #a5a5a5;
      font-size: 14px;
      line-height: 20px;
    }
  }

  .view-more-layout {
    position: relative;
    /*overflow-x: visible;*/
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5000;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
      background-color: transparent;
    }
  }

  .view-more-layout .view-more-component-container,
  .view-more-editor-component-container {
    width: 100%;
    position: relative;
    background-color: #1b1b1b;
  }

  .view-more-contents {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .view-more-component-container {
    &.fixed {
      position: fixed;
      margin: 0 !important;
      height: 100% !important;
      width: 100% !important;
    }
  }

  // 신규
  .inner {
    margin: 0 auto;
  }

  .top-section {
    height: 56px;
    padding-right: 12px;
  }

  .filter-wrapper {
    display: flex;
    justify-content: flex-end;
    margin: 0 2px;
    // height: 42px;
  }

  .side-filter-wrapper {
    padding: 12px 10px;
    &:first-child {
      z-index: 1;
    }
  }
</style>
